import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Check, Upload, MessageSquare, Trash2, FileIcon, Play, MessageCircle, Link2, ExternalLink, Sparkles, Receipt, PartyPopper, LayoutDashboard, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useSentryProject } from "@/hooks/use-sentry-project";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge, statusLabel, type ProjectStatus } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileReviewDialog } from "@/components/FileReviewDialog";
import { FreelancerAvatar } from "@/components/FreelancerAvatar";
import { ReviewPeriodCard } from "@/components/escrow/ReviewPeriodCard";
import { CountdownTimer } from "@/components/CountdownTimer";
import { useTranslation } from "react-i18next";
import { ProjectLargeFilesCard } from "@/components/ProjectLargeFilesCard";
import { GENERAL_FILE_MAX_BYTES } from "@/lib/large-files.config";
import { useServerFn } from "@tanstack/react-start";
import { notifyClientProjectCompleted } from "@/lib/project-notify.functions";
import { ProjectReviewCta } from "@/components/ProjectReviewDialog";


export const Route = createFileRoute("/_authenticated/projects/$id/")({
  head: () => ({
    meta: [
      { title: "Project workspace — Lumvio" },
      {
        name: "description",
        content:
          "Review project details, milestones, uploaded files, and invoice status inside your Lumvio project workspace.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ProjectDetail,
});

interface Project {
  id: string;
  freelancer_id: string;
  client_email: string;
  title: string;
  description: string | null;
  budget: number | null;
  status: ProjectStatus;
  client_assets_url: string | null;
  client_assets_note: string | null;
  review_period_ends_at: string | null;
  review_extension_hours: number | null;
  deadline: string | null;
  duration_days: number | null;
  completed_at: string | null;
  start_date: string | null;
}


interface Step {
  id: string;
  name: string;
  step_order: number;
  status: ProjectStatus;
  completed: boolean;
  completed_at: string | null;
}

interface ProjectFile {
  id: string;
  storage_path: string;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  created_at: string;
  uploader_id: string;
}

interface Comment {
  id: string;
  content: string;
  author_name: string | null;
  user_id: string;
  file_id: string | null;
  created_at: string;
}

function ProjectDetail() {
  const { t } = useTranslation();
  const { id } = useParams({ from: "/_authenticated/projects/$id/" });
  useSentryProject(id);
  const { user, profile } = useAuth();
  const qc = useQueryClient();

  const { data: project, isLoading } = useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("projects").select("*").eq("id", id).maybeSingle();
      if (error) throw error;
      return data as Project | null;
    },
  });

  const { data: steps } = useQuery({
    queryKey: ["steps", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("project_steps").select("*").eq("project_id", id).order("step_order");
      if (error) throw error;
      return data as Step[];
    },
  });

  const { data: files } = useQuery({
    queryKey: ["files", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("project_files").select("*").eq("project_id", id).order("created_at", { ascending: false });
      if (error) throw error;
      return data as ProjectFile[];
    },
  });

  const isOwner = !!(user && project && user.id === project.freelancer_id);

  const { data: projectInvoice } = useQuery({
    queryKey: ["project-invoice", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("id,status,escrow_status,amount,net_amount,partial_released_amount")
        .eq("project_id", id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as {
        id: string;
        status: string;
        escrow_status: string | null;
        amount: number | string;
        net_amount: number | string;
        partial_released_amount: number | string | null;
      } | null;
    },
  });

  const invoiceReleased =
    projectInvoice?.status === "released_to_freelancer" ||
    projectInvoice?.escrow_status === "released";
  const invoiceInEscrow =
    projectInvoice?.status === "paid_to_platform" && !invoiceReleased;


  const { data: freelancer } = useQuery({
    queryKey: ["project-freelancer", id],
    enabled: !!project && !isOwner,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("project_freelancer_public", { _project_id: id });
      if (error) throw error;
      return (data?.[0] ?? null) as {
        freelancer_id: string;
        full_name: string | null;
        avatar_url: string | null;
        kyc_status: "not_started" | "pending" | "verified" | "rejected";
        portfolio_username: string | null;
        portfolio_unlocked: boolean | null;
      } | null;
    },
  });

  const notifyCompleted = useServerFn(notifyClientProjectCompleted);
  const setStatus = useMutation({
    mutationFn: async (status: ProjectStatus) => {
      const { error } = await supabase.from("projects").update({ status }).eq("id", id);
      if (error) throw error;
      return status;
    },
    onSuccess: (status) => {
      toast.success(t("projectDetail.statusUpdated"));
      qc.invalidateQueries({ queryKey: ["project", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      if (status === "completed") {
        import("@/lib/posthog").then(({ capturePostHog, PHEvents }) =>
          capturePostHog(PHEvents.ProjectCompleted, { project_id: id }),
        ).catch(() => {});
        // Fire-and-forget email to the client
        notifyCompleted({ data: { projectId: id } }).catch((e) =>
          console.error("client notify failed", e),
        );
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <div className="py-24 text-center text-muted-foreground">{t("projectDetail.loading")}</div>;
  if (!project) return (
    <div className="py-24 text-center">
      <p className="text-muted-foreground">{t("projectDetail.notFound")}</p>
      <Button asChild variant="link"><Link to="/dashboard">{t("common.backToDashboard")}</Link></Button>
    </div>
  );

  return (
    <div className="space-y-10 animate-[fade-in_.4s_ease-out]">
      {/* Editorial header */}
      <header className="flex flex-col gap-6 border-b border-border pb-8 md:flex-row md:items-end md:justify-between">
        <div className="space-y-3">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" /> {t("projectDetail.backToProjects")}
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-serif text-4xl font-semibold tracking-tight md:text-5xl">{project.title}</h1>
            <StatusBadge status={project.status} />
          </div>
          {project.description && (
            <p className="max-w-2xl text-base text-muted-foreground">{project.description}</p>
          )}
          <div className="flex flex-wrap gap-x-8 gap-y-2 pt-1 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">{t("projectDetail.client")}</span>
              <span className="font-medium text-foreground">{project.client_email}</span>
            </div>
            {project.budget != null && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">{t("projectDetail.price")}</span>
                <span className="font-medium text-foreground">${Number(project.budget).toLocaleString()}</span>
              </div>
            )}
            {project.duration_days != null && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">{t("projectDetail.duration")}</span>
                <span className="font-medium text-foreground">{project.duration_days} {t("common.days")}</span>
              </div>
            )}
            {project.deadline && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">{t("projectDetail.deadline")}</span>
                <span className="font-medium text-foreground">
                  {new Date(project.deadline).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
            )}
          </div>
          <div className="pt-2">
            <CountdownTimer
              deadline={project.deadline}
              completedAt={project.completed_at}
              status={project.status}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/projects/$id/workspace"
            params={{ id }}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-background px-4 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <LayoutDashboard className="h-4 w-4" /> {t("projectDetail.workspace")}
          </Link>
          <Link
            to="/projects/$id/chat"
            params={{ id }}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-background px-4 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <MessageCircle className="h-4 w-4" /> {t("chat.openChat")}
          </Link>
          {isOwner && (
            <Select value={project.status} onValueChange={(v) => setStatus.mutate(v as ProjectStatus)}>
              <SelectTrigger className="h-10 w-48 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="brief">{t("projectDetail.status.brief")}</SelectItem>
                <SelectItem value="in_progress">{t("projectDetail.status.in_progress")}</SelectItem>
                <SelectItem value="in_review">{t("projectDetail.status.in_review")}</SelectItem>
                <SelectItem value="completed">{t("projectDetail.status.completed")}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </header>

      {isOwner && project.status === "completed" && user?.id && freelancer && (
        <ProjectReviewCta
          projectId={id}
          freelancerName={freelancer.full_name || t("projectDetail.yourFreelancerFallback")}
          freelancerId={project.freelancer_id}
          userId={user.id}
          variant="hero"
        />
      )}

      {project.status === "completed" && project.review_period_ends_at && (
        <ReviewPeriodCard

          projectId={id}
          reviewEndsAt={project.review_period_ends_at}
          extensionHours={project.review_extension_hours ?? 0}
          isClient={!isOwner}
          released={invoiceReleased}
          invoiceId={invoiceInEscrow ? projectInvoice?.id ?? null : null}
          netAmount={projectInvoice ? Number(projectInvoice.net_amount) : 0}
          partialReleased={projectInvoice ? Number(projectInvoice.partial_released_amount ?? 0) : 0}
        />
      )}


      {isOwner && project.status === "completed" && (
        <CompletedInvoiceCallout projectId={id} projectTitle={project.title} clientEmail={project.client_email} budget={project.budget} />
      )}


      <Pipeline steps={steps ?? []} currentStatus={project.status} />

      <div className="grid gap-8 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-7">
          <ClientAssetsSection project={project} isOwner={isOwner} />
          <FilesSection projectId={id} files={files ?? []} isOwner={isOwner} userId={user?.id ?? ""} authorName={profile?.full_name || profile?.email || "User"} />
          <ProjectLargeFilesCard projectId={id} />
        </div>
        <div className="space-y-6 lg:col-span-5">
          {!isOwner && freelancer && (
            <div className="surface p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{t("projectDetail.yourFreelancer")}</div>
              <div className="mt-3 flex items-center gap-4">
                <FreelancerAvatar
                  path={freelancer.avatar_url}
                  name={freelancer.full_name}
                  verified={freelancer.kyc_status === "verified"}
                  size="lg"
                />
                <div className="min-w-0">
                  <div className="truncate font-serif text-lg text-foreground">
                    {freelancer.full_name || t("projectDetail.freelancerFallback")}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {freelancer.kyc_status === "verified"
                      ? t("projectDetail.identityVerified")
                      : t("projectDetail.identityNotVerified")}
                  </div>
                </div>
              </div>
              {user?.id && (
                <ProjectReviewCta
                  projectId={id}
                  freelancerName={freelancer.full_name || t("projectDetail.yourFreelancerFallback")}
                  freelancerId={project.freelancer_id}
                  userId={user.id}
                />

              )}
            </div>
          )}
          <div className="lg:sticky lg:top-20">
            <CommentsSection projectId={id} userId={user?.id ?? ""} authorName={profile?.full_name || profile?.email || "User"} files={files ?? []} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Pipeline({ steps, currentStatus }: { steps: Step[]; currentStatus: ProjectStatus }) {
  const { t } = useTranslation();
  const order: ProjectStatus[] = ["brief", "in_progress", "in_review", "completed"];
  const currentIdx = order.indexOf(currentStatus);
  const percent = Math.round(((currentIdx + 1) / order.length) * 100);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{t("projectDetail.pipeline.title")}</h2>
        <span className="text-xs font-semibold text-primary">{t("projectDetail.pipeline.complete", { percent })}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {order.map((s, i) => {
          const done = i < currentIdx;
          const current = i === currentIdx;
          const step = steps.find((st) => st.status === s);
          const label = step?.name ?? statusLabel(s);
          return (
            <div key={s} className="relative group">
              {current && (
                <div className="pointer-events-none absolute -inset-0.5 rounded-2xl bg-primary opacity-15 blur transition duration-700 group-hover:opacity-25" />
              )}
              <div
                className={`relative rounded-2xl border p-4 transition-all ${
                  current
                    ? "border-primary/40 bg-card shadow-[var(--shadow-card)]"
                    : done
                    ? "border-success/30 bg-success/5"
                    : "border-border bg-card/40 opacity-70"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                      done
                        ? "bg-success text-success-foreground"
                        : current
                        ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {done ? <Check className="h-4 w-4" /> : i + 1}
                  </div>
                  <span className="font-serif font-semibold text-foreground">{label}</span>
                </div>
                <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full transition-all duration-700 ${done ? "bg-success w-full" : current ? "bg-primary w-full" : "w-0"}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


function ClientAssetsSection({ project, isOwner }: { project: Project; isOwner: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(project.client_assets_url ?? "");
  const [note, setNote] = useState(project.client_assets_note ?? "");

  const save = useMutation({
    mutationFn: async () => {
      const trimmed = url.trim();
      if (trimmed && !/^https?:\/\//i.test(trimmed)) {
        throw new Error(t("projectDetail.clientAssets.invalidUrl"));
      }
      const { error } = await supabase
        .from("projects")
        .update({
          client_assets_url: trimmed || null,
          client_assets_note: note.trim() || null,
        })
        .eq("id", project.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("projectDetail.clientAssets.saved"));
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["project", project.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const hasLink = !!project.client_assets_url;
  const canEdit = !isOwner; // clients edit; freelancer views

  return (
    <section className="rounded-3xl border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-all hover:shadow-[var(--shadow-elevated)]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">{t("projectDetail.clientAssets.title")}</h2>
        </div>
        {canEdit && hasLink && !editing && (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>{t("common.edit")}</Button>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {canEdit
          ? t("projectDetail.clientAssets.hintClient")
          : t("projectDetail.clientAssets.hintFreelancer")}
      </p>

      {hasLink && !editing ? (
        <div className="mt-4 space-y-3">
          <a
            href={project.client_assets_url!}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline break-all"
          >
            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            {project.client_assets_url}
          </a>
          {project.client_assets_note && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{project.client_assets_note}</p>
          )}
        </div>
      ) : canEdit ? (
        <div className="mt-4 space-y-3">
          <Input
            type="url"
            placeholder={t("projectDetail.clientAssets.urlPlaceholder")}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Textarea
            placeholder={t("projectDetail.clientAssets.notePlaceholder")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? t("common.saving") : t("projectDetail.clientAssets.sendLink")}
            </Button>
            {editing && (
              <Button size="sm" variant="ghost" onClick={() => {
                setEditing(false);
                setUrl(project.client_assets_url ?? "");
                setNote(project.client_assets_note ?? "");
              }}>{t("common.cancel")}</Button>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {t("projectDetail.clientAssets.waiting")}
        </div>
      )}
    </section>
  );
}


function FilesSection({ projectId, files, isOwner, userId, authorName }: { projectId: string; files: ProjectFile[]; isOwner: boolean; userId: string; authorName: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [reviewFile, setReviewFile] = useState<ProjectFile | null>(null);
  const { user } = useAuth();

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > GENERAL_FILE_MAX_BYTES) {
      toast.error(t("projectDetail.largeFiles.generalTooLarge"));
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const path = `${projectId}/${Date.now()}-${file.name.replace(/[^\w.-]/g, "_")}`;
      const { error: upErr } = await supabase.storage.from("project-files").upload(path, file);
      if (upErr) throw upErr;
      const { error: dbErr } = await supabase.from("project_files").insert({
        project_id: projectId,
        uploader_id: user.id,
        storage_path: path,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
      });
      if (dbErr) throw dbErr;
      toast.success(t("projectDetail.files.uploaded"));
      qc.invalidateQueries({ queryKey: ["files", projectId] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("projectDetail.files.uploadFailed"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const deleteFile = useMutation({
    mutationFn: async (f: ProjectFile) => {
      await supabase.storage.from("project-files").remove([f.storage_path]);
      const { error } = await supabase.from("project_files").delete().eq("id", f.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("projectDetail.files.removed"));
      qc.invalidateQueries({ queryKey: ["files", projectId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="rounded-3xl border border-border/70 bg-card/70 p-6 shadow-[var(--shadow-card)] backdrop-blur-xl transition-all hover:shadow-[var(--shadow-elevated)]">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-primary/25 to-primary/5 text-primary ring-1 ring-primary/20">
            <Upload className="h-4.5 w-4.5" aria-hidden="true" />
          </div>
          <h2 className="truncate text-lg font-semibold tracking-tight">{t("projectDetail.files.title")}</h2>
        </div>
        {isOwner && (
          <>
            <input ref={inputRef} type="file" className="hidden" accept="image/*,video/*" onChange={handleUpload} />
            <Button size="sm" className="shrink-0 rounded-xl" onClick={() => inputRef.current?.click()} disabled={uploading}>
              <Upload className="h-4 w-4" /> {uploading ? t("common.uploading") : t("projectDetail.files.upload")}
            </Button>
          </>
        )}
      </div>

      {files.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border/70 bg-background/40 p-10 text-center text-sm text-muted-foreground">
          {t("projectDetail.files.empty")}
        </div>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {files.map((f) => (
            <FileCard
              key={f.id}
              file={f}
              isOwner={isOwner}
              onDelete={() => deleteFile.mutate(f)}
              onReview={() => setReviewFile(f)}
            />
          ))}
        </div>
      )}

      <FileReviewDialog
        file={reviewFile ? { id: reviewFile.id, storage_path: reviewFile.storage_path, file_name: reviewFile.file_name, file_type: reviewFile.file_type, project_id: projectId } : null}
        open={!!reviewFile}
        onOpenChange={(v) => { if (!v) setReviewFile(null); }}
        userId={userId}
        authorName={authorName}
      />
    </section>
  );
}

function FileCard({ file, isOwner, onDelete, onReview }: { file: ProjectFile; isOwner: boolean; onDelete: () => void; onReview: () => void }) {
  const { t } = useTranslation();
  const { data: url } = useQuery({
    queryKey: ["file-url", file.id, file.storage_path],
    queryFn: async () => {
      const { data, error } = await supabase.storage.from("project-files").createSignedUrl(file.storage_path, 3600);
      if (error) throw error;
      return data.signedUrl;
    },
    staleTime: 30 * 60 * 1000,
  });

  const isImage = file.file_type?.startsWith("image/");
  const isVideo = file.file_type?.startsWith("video/");

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-background group">
      <button
        type="button"
        onClick={onReview}
        className="relative aspect-video bg-muted w-full flex items-center justify-center overflow-hidden"
        title={t("projectDetail.files.openReview")}
      >
        {url && isImage ? (
          <img src={url} alt={file.file_name} className="h-full w-full object-cover" />
        ) : url && isVideo ? (
          <video src={url} className="h-full w-full object-cover" muted playsInline />
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            {isVideo ? <Play className="h-8 w-8" /> : <FileIcon className="h-8 w-8" />}
            <span className="text-xs">{t("projectDetail.files.previewUnavailable")}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <span className="inline-flex items-center gap-1 rounded-full bg-white/95 text-black text-xs font-semibold px-3 py-1.5">
            <MessageCircle className="h-3.5 w-3.5" /> {t("projectDetail.files.reviewComment")}
          </span>
        </div>
      </button>
      <div className="flex items-center justify-between gap-2 p-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{file.file_name}</div>
          <div className="text-xs text-muted-foreground">
            {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : ""}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onReview}>
            <MessageCircle className="h-4 w-4" /> {t("projectDetail.files.review")}
          </Button>
          {isOwner && (
            <Button size="sm" variant="ghost" onClick={onDelete}><Trash2 className="h-4 w-4" /></Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CommentsSection({ projectId, userId, authorName, files }: { projectId: string; userId: string; authorName: string; files: ProjectFile[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [content, setContent] = useState("");
  const [fileId, setFileId] = useState<string>("general");

  const { data: comments } = useQuery({
    queryKey: ["comments", projectId],
    queryFn: async () => {
      const { data, error } = await supabase.from("comments").select("*").eq("project_id", projectId).order("created_at", { ascending: false });
      if (error) throw error;
      return data as Comment[];
    },
  });

  const add = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("comments").insert({
        project_id: projectId,
        user_id: userId,
        author_name: authorName,
        content: content.trim(),
        file_id: fileId === "general" ? null : fileId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setContent("");
      qc.invalidateQueries({ queryKey: ["comments", projectId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="rounded-3xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <h2 className="font-serif text-lg font-semibold">{t("projectDetail.feedback.title")}</h2>
        </div>
        <span className="text-xs font-medium text-muted-foreground">
          {t((comments ?? []).length === 1 ? "projectDetail.feedback.one" : "projectDetail.feedback.other", { count: (comments ?? []).length })}
        </span>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); if (content.trim()) add.mutate(); }}
        className="mt-4 space-y-2"
      >
        {files.length > 0 && (
          <Select value={fileId} onValueChange={setFileId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="general">{t("projectDetail.feedback.general")}</SelectItem>
              {files.map((f) => (
                <SelectItem key={f.id} value={f.id}>{t("projectDetail.feedback.onFile", { name: f.file_name })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t("projectDetail.feedback.placeholder")}
          rows={3}
          required
        />
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={add.isPending || !content.trim()}>
            {add.isPending ? t("common.posting") : t("common.post")}
          </Button>
        </div>
      </form>

      <div className="mt-6 space-y-3">
        {(comments ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("projectDetail.feedback.empty")}</p>
        ) : (
          comments!.map((c) => {
            const file = files.find(f => f.id === c.file_id);
            return (
              <div key={c.id} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{c.author_name || t("common.user")}</span>
                  <span>{new Date(c.created_at).toLocaleString()}</span>
                </div>
                {file && (
                  <div className="mt-1 text-xs text-primary">{t("projectDetail.feedback.on", { name: file.file_name })}</div>
                )}
                <p className="mt-2 text-sm whitespace-pre-wrap">{c.content}</p>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function CompletedInvoiceCallout({
  projectId,
  projectTitle,
  clientEmail,
  budget,
}: {
  projectId: string;
  projectTitle: string;
  clientEmail: string;
  budget: number | null;
}) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const { data: existingInvoice } = useQuery({
    queryKey: ["project-invoice", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("id,status")
        .eq("project_id", projectId)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as { id: string; status: string } | null;
    },
  });

  if (dismissed) return null;

  const hasInvoice = !!existingInvoice;
  const invoiceParams = new URLSearchParams({
    project_id: projectId,
    title: projectTitle,
    client_email: clientEmail,
    ...(budget != null ? { amount: String(budget) } : {}),
  });

  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background to-background p-6 shadow-[0_0_40px_-10px_hsl(var(--primary)/0.35)] animate-[fade-in_.5s_ease-out]">
      <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <Sparkles className="absolute left-8 top-6 h-4 w-4 animate-pulse text-primary" />
        <Sparkles className="absolute right-16 bottom-8 h-3 w-3 animate-pulse text-primary [animation-delay:400ms]" />
        <Sparkles className="absolute left-1/2 top-4 h-3 w-3 animate-pulse text-primary [animation-delay:800ms]" />
      </div>

      <div className="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 ring-1 ring-primary/30">
            <PartyPopper className="h-6 w-6 text-primary" />
          </div>
          <div className="space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
              {t("projectDetail.completed.badge")}
            </div>
            <h3 className="font-serif text-2xl text-foreground">
              {hasInvoice ? t("projectDetail.completed.hasInvoiceTitle") : t("projectDetail.completed.noInvoiceTitle")}
            </h3>
            <p className="max-w-xl text-sm text-muted-foreground">
              {hasInvoice
                ? t("projectDetail.completed.hasInvoiceBody")
                : t("projectDetail.completed.noInvoiceBody", { email: clientEmail })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 md:shrink-0">
          <Button variant="ghost" onClick={() => setDismissed(true)} className="text-muted-foreground hover:text-foreground">
            {t("common.later")}
          </Button>
          <Link
            to="/invoices"
            search={hasInvoice ? undefined : (Object.fromEntries(invoiceParams) as never)}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)] transition-transform hover:scale-[1.02]"
          >
            <Receipt className="h-4 w-4" />
            {hasInvoice ? t("projectDetail.completed.viewInvoice") : t("projectDetail.completed.createInvoice")}
          </Link>
        </div>
      </div>
    </div>
  );
}
